import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";
import { IUser } from "../../Models/User";
import { buildQueryParam } from "utils/filtering";
import {
  UserPaginationFiltersForQuery,
} from "Interfaces/UserFilters";
import { getQueryParams } from "utils/getQueryParams";
import { IGenericResponse, IPaginatedList, IPaginationQuery } from "Interfaces/PaginatedList";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: customFetchBase,
  tagTypes: ["Users", "Patients"],
  keepUnusedDataFor: 300,  // Keep cached data for 5 minutes globally
  refetchOnReconnect: true, // Refetch data when reconnected to the network
  refetchOnFocus: true, // Refetch data when the window is focused again
  endpoints: (builder) => ({
    getAllUsers: builder.query<IUser[], UserPaginationFiltersForQuery | void>({
      query(filters: UserPaginationFiltersForQuery) {
        const roleQueryParam = buildQueryParam("roles", filters?.role?.value);

        const queryParams = [roleQueryParam].filter(Boolean).join("&");

        const url = queryParams ? `users/all?${queryParams}` : "users/all";

        return {
          url,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["Users"],
      transformResponse: (results: { data: IUser[] }) => {
        return results.data;
      },
    }),

    getUsers: builder.query<
      IPaginatedList<IUser>,
      IPaginationQuery<UserPaginationFiltersForQuery>
    >({
      query(queryFilters) {

        return {
          url: `${getQueryParams(`users`, queryFilters)}&`,
          credentials: "include",
        };
      },
      transformResponse: (results: IGenericResponse<IPaginatedList<IUser>>) =>{
        return results.data
      },
      providesTags: () => ["Users"],
    }),

    // Get single user

    getUser: builder.query<IUser, string>({
      query(userId) {
        return {
          url: "users/" + userId,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["Users"],
      transformResponse: (result: { data: IUser }) => {
        return result.data;
      },
    }),
    // Get single user

    getUserRelativeByMail: builder.query<IUser, string>({
      query(email) {
        return {
          url: "users/relatives/" + email,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["Users"],
      transformResponse: (result: { data: IUser }) => {
        return result.data;
      },
    }),
    // Post user

    postUser: builder.mutation<IUser, FormData>({
      query(newUser) {
        return {
          url: "users",
          method: "POST",
          body: newUser,
          credentials: "include",
        };
      },
      invalidatesTags: ["Users",],
      transformResponse: (result: { user: { _doc: IUser } }) => {
        return result.user._doc;
      },
    }),

    sendActivationEmail: builder.mutation<IUser, string>({
      query(userId) {
        return {
          url: "users/verification-user-email/" + userId,
          method: "PUT",
          credentials: "include",
        };
      },
      invalidatesTags: ["Users"],
      transformResponse: (result: IUser) => {
        return result;
      }
    }),

    uploadUser: builder.mutation<IUser[], FormData>({
      query(newUser) {
        return {
          url: "users/upload",
          method: "POST",
          body: newUser,
          credentials: "include",
        };
      },
      invalidatesTags: ["Users"],
      transformResponse: (result: { data: IUser[] }) => {
        return result.data;
      },
    }),

    // Update user

    updateUser: builder.mutation<
      IUser,
      { userId: string; updatedUser: FormData }
    >({
      query({ userId, updatedUser }) {
        return {
          url: "users/" + userId,
          method: "PUT",
          body: updatedUser,
          credentials: "include",
        };
      },
      invalidatesTags: ["Users",],
      transformResponse: (result: { data: { _doc: IUser } }) => {
        return result.data._doc;
      },
    }),

    // Delete user

    deleteUser: builder.mutation<IUser, string>({
      query(userId) {
        return {
          url: "users/" + userId,
          method: "DELETE",
          credentials: "include",
        };
      },
      invalidatesTags: ["Users"],
      transformResponse: (result: { data: IUser }) => {
        return result.data;
      },
    }),
  }),
});

export const {
  useGetAllUsersQuery,
  useGetUserQuery,
  useGetUsersQuery,
  useGetUserRelativeByMailQuery,
  useUpdateUserMutation,
  usePostUserMutation,
  useSendActivationEmailMutation,
  useUploadUserMutation,
  useDeleteUserMutation,
} = userApi;
